import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "../../axiosInstace";
import { toast } from "react-toastify";
import { useWorkspace } from "../../contexts/Workspacecontext";

/* ─── DEFAULT ANNEXURE ─────────────────────────────────────────────────── */
const DEFAULT_ANNEXURE = {
  recruitmentFee: "8.33",
  paymentDays: "35",
  replacementDays: "90",
  replacementDeadlineDays: "30",
  noticePeriod: "30",
  agreementValidity: "3 Years",
  jurisdiction: "Bangalore, Karnataka",
};

const DEFAULT_FIELDS = {
  day: new Date().getDate().toString(),
  month: new Date().toLocaleString("en-IN", { month: "long" }),
  year: new Date().getFullYear().toString(),
  // Company (owner) side
  companyName: "Federate Consulting Pvt Ltd",
  companyAddress:
    "134, 135, 2nd Floor LRDE Layout, Karthik Nagar, Doddanekundi, Bengaluru, Karnataka 560037",
  companyNoticeName: "",
  companyNoticeAddress: "",
  companyNoticeEmail: "",
  companySignatoryName: "",
  companySignatoryDesignation: "",
  // Consultant (partner) side
  consultancyName: "",
  consultancyType: "Proprietorship",
  consultancyAddress: "",
  consultancyNoticeName: "",
  consultancyNoticeAddress: "",
  consultancyNoticeEmail: "",
  consultancySignatoryName: "",
  consultancySignatoryDesignation: "",
};

/* ─── MONTHS ───────────────────────────────────────────────────────────── */
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/* ─── HTML BUILDER ─────────────────────────────────────────────────────── */
function buildAgreementHTML(f, annexure) {
  const v = (val, fb = "___________") =>
    val && String(val).trim() ? `<strong>${val}</strong>` : `<u>${fb}</u>`;

  return `
<div style="font-family:Georgia,'Times New Roman',serif;font-size:13px;line-height:1.85;color:#1a1a1a;max-width:800px;margin:0 auto;padding:24px 32px;">

  <h2 style="text-align:center;font-size:16px;font-weight:bold;margin:0 0 4px;letter-spacing:0.5px;">RECRUITMENT CONSULTANCY AGREEMENT</h2>
  <p style="text-align:center;font-size:12px;color:#555;margin:0 0 18px;">
    This Recruitment Consultancy Agreement ("Agreement") is made and executed on this
    ${v(f.day, "__")} day of ${v(f.month, "__________")} ${v(f.year, "20__")} at Bangalore, Karnataka.
  </p>

  <p style="margin:0 0 6px;"><strong>between</strong></p>
  <p style="margin:0 0 10px;">
    ${v(f.companyName, "Federate Consulting Pvt Ltd")}, a company incorporated under the provisions of the Companies Act, 2013,
    having its registered office at ${v(f.companyAddress, "134, 135, 2nd Floor LRDE Layout, Karthik Nagar, Doddanekundi, Bengaluru, Karnataka 560037")},
    hereinafter referred to as the <strong>"Company"</strong> (which expression shall unless repugnant to the context or meaning thereof
    include its successors, affiliates, group companies, representatives, and permitted assigns);
  </p>
  <p style="margin:0 0 6px;"><strong>AND</strong></p>
  <p style="margin:0 0 16px;">
    ${v(f.consultancyName, "[CONSULTANCY NAME]")}, a ${v(f.consultancyType, "proprietorship/partnership/private limited company")}
    having its office at ${v(f.consultancyAddress, "________________________________")},
    hereinafter referred to as the <strong>"Consultant"</strong> or <strong>"Recruitment Consultancy"</strong>
    (which expression shall unless repugnant to the context or meaning thereof include its successors and permitted assigns).
  </p>
  <p style="margin:0 0 16px;">The Company and Consultant shall individually be referred to as a <strong>"Party"</strong> and collectively as the <strong>"Parties"</strong>.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">1. PURPOSE OF AGREEMENT</h3>
  <p style="margin:0 0 10px;">The Company appoints the Consultant on a non-exclusive basis to provide recruitment and talent acquisition services for positions assigned by the Company from time to time, subject to the terms and conditions contained herein.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">2. SCOPE OF SERVICES</h3>
  <p style="margin:0 0 4px;">The Consultant shall:</p>
  <ul style="margin:0 0 8px;padding-left:22px;">
    <li>Source suitable candidates based on job descriptions shared by the Company.</li>
    <li>Conduct preliminary screening and validation of profiles.</li>
    <li>Share resumes of qualified candidates with the Company.</li>
    <li>Coordinate interview schedules between candidates and the Company representative / Interviewers.</li>
    <li>Support closure of selected candidates.</li>
    <li>Maintain professional conduct while representing the Company.</li>
  </ul>
  <p style="margin:0 0 4px;">The Consultant shall <strong>not</strong>:</p>
  <ul style="margin:0 0 8px;padding-left:22px;">
    <li>Make commitments on behalf of the Company unless expressly authorized in writing.</li>
    <li>Release or ask confidential information regarding salary structures, business strategies, or clients.</li>
    <li>Use the Company's name/logo for advertisements or marketing without written approval.</li>
  </ul>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">3. ENGAGEMENT MODEL</h3>
  <p style="margin:0 0 4px;">This Agreement shall operate on a requisition basis. The Company may share hiring requirements at its sole discretion.</p>
  <p style="margin:0 0 4px;">The Company shall have full authority to:</p>
  <ul style="margin:0 0 8px;padding-left:22px;">
    <li>Accept or reject any candidate.</li>
    <li>Put requirements on hold.</li>
    <li>Close positions internally.</li>
    <li>Work simultaneously with other consultants/agencies.</li>
  </ul>
  <p style="margin:0 0 10px;">No exclusivity is granted to the Consultant unless specifically agreed in writing.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">4. CONSULTANCY FEES</h3>
  <p style="margin:0 0 4px;"><strong>4.1 Standard Fee</strong></p>
  <p style="margin:0 0 8px;">The Company shall pay the Consultant a professional fee specified in the annexure 1 of the selected candidate's Gross Salary (${annexure.recruitmentFee}% of Annual Gross Salary).</p>
  <p style="margin:0 0 4px;"><strong>4.2 Fee Applicability</strong></p>
  <p style="margin:0 0 4px;">Fees shall become payable only if:</p>
  <ul style="margin:0 0 8px;padding-left:22px;">
    <li>The candidate joins the Company; and</li>
    <li>The candidate's profile was first introduced by the Consultant; and</li>
    <li>The candidate was not already available in the Company database, internal references, career/job portal, social media applications, or through another consultant.</li>
  </ul>
  <p style="margin:0 0 4px;">The Company's records shall be final in determining candidate ownership.</p>
  <p style="margin:0 0 4px;"><strong>4.3 Duplicate Profiles</strong></p>
  <p style="margin:0 0 10px;">In case the same candidate profile is received from multiple sources, the fee shall be payable only to the source that first submitted the complete profile with candidate consent. The Company reserves the right to determine ownership based on timestamped records.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">5. PAYMENT TERMS</h3>
  <p style="margin:0 0 4px;">Payment shall be made within <strong>${annexure.paymentDays} days</strong> from:</p>
  <ul style="margin:0 0 8px;padding-left:22px;">
    <li>Candidate joining date; and</li>
    <li>Submission of valid invoice by the Consultant.</li>
  </ul>
  <p style="margin:0 0 4px;">All payments shall be subject to:</p>
  <ul style="margin:0 0 8px;padding-left:22px;">
    <li>Applicable TDS deductions under Income Tax Act, 1961.</li>
    <li>Submission of GST invoice (if applicable).</li>
  </ul>
  <p style="margin:0 0 10px;">No advance payments shall be made. In case the candidate does not join, no fee shall be payable. Any reimbursement of expenses shall require prior written approval from the Company.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">6. FREE REPLACEMENT GUARANTEE</h3>
  <p style="margin:0 0 4px;"><strong>6.1 Replacement Period</strong></p>
  <p style="margin:0 0 4px;">If the selected candidate resigns, absconds, is terminated for misconduct, fails background verification, or leaves employment for any reason within <strong>${annexure.replacementDays} days</strong> from joining date, the Consultant shall provide one suitable replacement candidate at no additional cost.</p>
  <p style="margin:0 0 4px;"><strong>6.2 Refund Restriction</strong></p>
  <p style="margin:0 0 10px;">The Company shall not be obligated to pay additional fees for replacement hiring. If replacement is not provided within <strong>${annexure.replacementDeadlineDays} days</strong>, the Company reserves the right to seek proportionate refund or adjustment against future invoices.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">7. CANDIDATE AUTHENTICITY &amp; LIABILITY</h3>
  <p style="margin:0 0 10px;">The Consultant confirms that all resumes are shared with candidate consent, candidate information is true to the best knowledge of the Consultant, and no fake, manipulated, or misleading information shall knowingly be submitted. The Consultant shall be liable for losses arising from knowingly providing fraudulent or fabricated candidate information.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">8. NON-SOLICITATION</h3>
  <p style="margin:0 0 10px;">The Consultant shall not directly or indirectly approach, solicit, or recruit employees of the Company for any third party during the term of this Agreement and for a period of <strong>24 months</strong> thereafter; induce Company employees to resign; or share Company employee data with external parties. Violation shall make the Consultant liable for damages, legal action, and immediate termination of this Agreement.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">9. CONFIDENTIALITY</h3>
  <p style="margin:0 0 10px;">The Consultant acknowledges that during the course of engagement it may receive confidential information including but not limited to business plans, compensation structures, client details, employee information, hiring plans, technical information, and internal processes. The Consultant agrees to maintain strict confidentiality, not to disclose information to any third party, and not to misuse Company information for personal or commercial benefit. <strong>This clause shall survive termination of the Agreement.</strong></p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">10. DATA PRIVACY &amp; COMPLIANCE</h3>
  <p style="margin:0 0 10px;">The Consultant shall comply with applicable Indian laws relating to data privacy, information technology, employment regulations, and anti-discrimination provisions. Candidate data shared by the Company shall not be stored, reused, or circulated for unrelated opportunities without consent.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">11. BACKGROUND VERIFICATION</h3>
  <p style="margin:0 0 10px;">The Company reserves the right to conduct background verification checks on all candidates. If any material discrepancy is identified, the offer may be revoked, employment may be terminated, and consultancy fee may be withheld or recovered.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">12. INDEPENDENT CONTRACTOR</h3>
  <p style="margin:0 0 10px;">The Consultant shall act as an independent contractor. Nothing in this Agreement shall create an employer-employee relationship, partnership, agency, or joint venture. The Consultant shall be solely responsible for its employees, taxes, statutory obligations, and operational expenses.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">13. LIMITATION OF LIABILITY</h3>
  <p style="margin:0 0 10px;">The Company shall not be liable for any indirect or consequential damages, business losses, Consultant operational costs, or candidate disputes. The Company's maximum liability under this Agreement shall not exceed the fee paid for the specific candidate concerned.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">14. INDEMNITY</h3>
  <p style="margin:0 0 10px;">The Consultant agrees to indemnify and hold harmless the Company against misrepresentation by candidates knowingly concealed by Consultant; third-party claims arising from Consultant misconduct; breach of confidentiality; violation of applicable laws; and unauthorized use of Company information.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">15. TERMINATION</h3>
  <p style="margin:0 0 10px;">The Company may terminate this Agreement without cause by giving <strong>${annexure.noticePeriod} days</strong> written notice, or immediately in case of misconduct, fraud, confidentiality breach, non-performance, or ethical violations. Upon termination, pending payments for successful joined candidates shall be processed subject to Agreement terms.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">16. FORCE MAJEURE</h3>
  <p style="margin:0 0 10px;">Neither Party shall be liable for delays or failures caused due to events beyond reasonable control including natural disasters, government restrictions, pandemic, war, cyber incidents, or strikes.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">17. GOVERNING LAW &amp; JURISDICTION</h3>
  <p style="margin:0 0 10px;">This Agreement shall be governed by the laws of India. The courts at <strong>${annexure.jurisdiction}</strong> shall have exclusive jurisdiction over disputes arising from this Agreement.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">18. DISPUTE RESOLUTION</h3>
  <p style="margin:0 0 10px;">The Parties shall first attempt amicable resolution through discussion. If unresolved within 30 days, disputes shall be referred to arbitration under the Arbitration and Conciliation Act, 1996. Venue: <strong>${annexure.jurisdiction}</strong>. Language: English. Number of arbitrators: One. The arbitrator's decision shall be final and binding.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">19. NOTICE</h3>
  <p style="margin:0 0 8px;">All notices shall be sent via registered post, courier, or official email communication.</p>
  <table style="width:100%;border-collapse:collapse;font-size:12px;margin-top:8px;">
    <tbody>
      <tr>
        <td style="vertical-align:top;width:50%;padding-right:16px;border-right:1px solid #e5e7eb;">
          <strong>Company:</strong><br/>
          Name: ${v(f.companyNoticeName)}<br/>
          Address: ${v(f.companyNoticeAddress)}<br/>
          Email: ${v(f.companyNoticeEmail)}
        </td>
        <td style="vertical-align:top;padding-left:16px;">
          <strong>Consultant:</strong><br/>
          Name: ${v(f.consultancyNoticeName)}<br/>
          Address: ${v(f.consultancyNoticeAddress)}<br/>
          Email: ${v(f.consultancyNoticeEmail)}
        </td>
      </tr>
    </tbody>
  </table>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">20. ENTIRE AGREEMENT</h3>
  <p style="margin:0 0 10px;">This Agreement constitutes the complete understanding between the Parties and supersedes all prior discussions, communications, or understandings. Any amendment shall be valid only if made in writing and signed by both Parties.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">21. VALIDITY</h3>
  <p style="margin:0 0 10px;">This Agreement shall remain valid for a period of <strong>${annexure.agreementValidity}</strong> from the Effective Date unless terminated earlier in accordance with this Agreement.</p>

  <h3 style="font-size:13px;font-weight:bold;margin:18px 0 6px;">22. ACCEPTANCE &amp; SIGNATURES</h3>
  <p style="margin:0 0 16px;">IN WITNESS WHEREOF, the Parties hereto have executed this Agreement on the date first mentioned above.</p>
  <div style="display:flex;justify-content:space-between;gap:40px;margin-top:20px;">
    <div style="flex:1;border-top:1.5px solid #555;padding-top:10px;font-size:12px;">
      <strong>For the Company</strong><br/>
      Name: ${v(f.companySignatoryName)}<br/>
      Designation: ${v(f.companySignatoryDesignation)}<br/>
      Seal &amp; Signature: _______________
    </div>
    <div style="flex:1;border-top:1.5px solid #555;padding-top:10px;font-size:12px;">
      <strong>For the Recruitment Consultancy</strong><br/>
      Name: ${v(f.consultancySignatoryName)}<br/>
      Designation: ${v(f.consultancySignatoryDesignation)}<br/>
      Seal &amp; Signature: _______________
    </div>
  </div>

  <h3 style="font-size:13px;font-weight:bold;margin:28px 0 8px;border-top:2px solid #333;padding-top:14px;">ANNEXURE 1 – STANDARD TERMS</h3>
  <table style="width:100%;border-collapse:collapse;font-size:12px;">
    <thead>
      <tr style="background:#f3f4f6;">
        <th style="padding:8px 12px;border:1px solid #d1d5db;text-align:left;font-weight:700;">Particulars</th>
        <th style="padding:8px 12px;border:1px solid #d1d5db;text-align:left;font-weight:700;">Details</th>
      </tr>
    </thead>
    <tbody>
      ${[
        [
          "Recruitment Fee",
          `${annexure.recruitmentFee}% of Annual Gross Salary`,
        ],
        ["Payment Terms", `Within ${annexure.paymentDays} days of joining`],
        ["Replacement Period", `${annexure.replacementDays} days`],
        [
          "Replacement to be made within",
          `${annexure.replacementDeadlineDays} days`,
        ],
        ["Notice Period for Termination", `${annexure.noticePeriod} days`],
        ["Agreement Validity", annexure.agreementValidity],
        ["Jurisdiction", annexure.jurisdiction],
        ["GST Applicability", "Extra as applicable"],
        ["TDS Deduction", "As per Income Tax Act"],
      ]
        .map(
          ([k, val]) =>
            `<tr><td style="padding:7px 12px;border:1px solid #d1d5db;color:#374151;">${k}</td><td style="padding:7px 12px;border:1px solid #d1d5db;font-weight:600;color:#111827;">${val}</td></tr>`,
        )
        .join("")}
    </tbody>
  </table>
</div>`.trim();
}

/* ─── PDF GENERATOR ────────────────────────────────────────────────────── */
export async function generatePDFBlob(html) {
  const html2pdf = (await import("html2pdf.js")).default;

  return new Promise((resolve, reject) => {
    const container = document.createElement("div");
    container.style.cssText = "width:210mm; padding: 0; margin: 0;";
    container.innerHTML = html;
    document.body.appendChild(container);

    html2pdf()
      .set({
        margin: [12, 12, 12, 12],
        filename: "recruitment-agreement.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
          scrollX: 0,
          scrollY: 0,
          windowWidth: container.scrollWidth,
          windowHeight: container.scrollHeight,
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      })
      .from(container)
      .outputPdf("blob")
      .then((pdfBlob) => {
        document.body.removeChild(container);
        resolve(pdfBlob);
      })
      .catch((err) => {
        if (document.body.contains(container))
          document.body.removeChild(container);
        reject(err);
      });
  });
}

/* ─── SEND TO API ──────────────────────────────────────────────────────── */
export async function submitAgreement({
  moduleHistoryId,
  pdfBlob,
  senderDocumentStatus,
  isDraft,
  hrBase,
}) {
  if (!pdfBlob || pdfBlob.size === 0)
    throw new Error("PDF generation failed — blob is empty.");

  const formData = new FormData();

  // ✅ Flat fields — matches @ModelAttribute binding
  formData.append("moduleHistoryId", moduleHistoryId);
  formData.append("senderDocumentStatus", senderDocumentStatus);
  formData.append("draft", String(isDraft));

  formData.append(
    "file",
    new File([pdfBlob], "recruitment-agreement.pdf", {
      type: "application/pdf",
    }),
  );

  return axios.put(`${hrBase}/module-terms`, formData);
}

const INP_STYLE = {
  display: "inline-block",
  padding: "1px 6px",
  border: "none",
  borderBottom: "1.5px solid #2563eb",
  background: "#eff6ff",
  borderRadius: "3px 3px 0 0",
  fontSize: 12,
  color: "#1e3a8a",
  fontFamily: "Georgia, serif",
  outline: "none",
  fontWeight: 600,
};
const SEL_STYLE = { ...INP_STYLE, cursor: "pointer" };
const SM_STYLE = { ...INP_STYLE, width: 48, textAlign: "center" };

/* ─── REUSABLE INPUT COMPONENTS (outside component to prevent remount) ─── */
const Blank = ({ fKey, value, onChange, placeholder, width = 140 }) => (
  <input
    value={value}
    onChange={(e) => onChange(fKey)(e.target.value)}
    placeholder={placeholder || "___________"}
    style={{ ...INP_STYLE, width }}
  />
);

const Wide = ({ fKey, value, onChange, placeholder, width = 220 }) => (
  <input
    value={value}
    onChange={(e) => onChange(fKey)(e.target.value)}
    placeholder={placeholder}
    style={{ ...INP_STYLE, width }}
  />
);

const ABlank = ({
  aKey,
  value,
  onChange,
  type = "text",
  suffix,
  placeholder,
  width = 72,
}) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(aKey)(e.target.value)}
      placeholder={placeholder}
      style={{ ...INP_STYLE, width }}
    />
    {suffix && <span style={{ fontSize: 11, color: "#6b7280" }}>{suffix}</span>}
  </span>
);

/* ─── SPINNER ────────────────────────────────────────────────────────────── */
const Spin = ({ white }) => (
  <div
    style={{
      width: 14,
      height: 14,
      borderRadius: "50%",
      border: `2px solid ${white ? "rgba(255,255,255,0.35)" : "#d1d5db"}`,
      borderTopColor: white ? "#fff" : "#374151",
      animation: "ra-spin 0.65s linear infinite",
      display: "inline-block",
      flexShrink: 0,
    }}
  />
);

/* ─── BUTTON ─────────────────────────────────────────────────────────────── */
const Btn = ({ v = "primary", disabled, loading: ld, onClick, children }) => {
  const S = {
    primary: { bg: "#2563eb", color: "#fff", border: "#2563eb" },
    success: { bg: "#15803d", color: "#fff", border: "#15803d" },
    amber: { bg: "#fef3c7", color: "#d97706", border: "#fbbf24" },
    outline: { bg: "#eff6ff", color: "#2563eb", border: "#2563eb" },
    ghost: { bg: "transparent", color: "#374151", border: "#d1d5db" },
    neutral: { bg: "#f3f4f6", color: "#374151", border: "#d1d5db" },
  };
  const s = S[v] || S.primary;
  return (
    <button
      disabled={disabled || ld}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "8px 16px",
        borderRadius: 20,
        fontSize: 13,
        fontWeight: 600,
        border: `1.5px solid ${s.border}`,
        background: s.bg,
        color: s.color,
        cursor: disabled || ld ? "not-allowed" : "pointer",
        opacity: disabled && !ld ? 0.5 : 1,
        fontFamily: "inherit",
        whiteSpace: "nowrap",
        transition: "opacity .15s",
      }}
    >
      {ld && <Spin white={v === "primary" || v === "success"} />}
      {children}
    </button>
  );
};

/* ══════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════ */
export const RecruitmentAgreementModal = ({
  moduleHistoryId,
  connectionId,
  isResend = false,
  rejectionReason = null,
  prefillItem = null, // ← new: pass the raw item object for immediate prefill
  onSave,
  onClose,
}) => {
  const { isConsultant } = useWorkspace();

  const [step, setStep] = useState(1);
  const [f, setF] = useState({ ...DEFAULT_FIELDS });
  const [annexure, setAnnexure] = useState({ ...DEFAULT_ANNEXURE });
  const [loading, setLoading] = useState(true); // initial data fetch
  const [busy, setBusy] = useState(false); // PDF generation + submit
  const [busyType, setBusyType] = useState(""); // "draft" | "send" | "resend"
  const [prefillReady, setPrefillReady] = useState(!isResend); // false while resend is fetching/parsing

  const set = (key) => (val) => setF((p) => ({ ...p, [key]: val }));
  const setA = (key) => (val) => setAnnexure((p) => ({ ...p, [key]: val }));

  const HR_BASE = useMemo(
    () =>
      isConsultant
        ? `${process.env.REACT_APP_API_CONSULTANT_ADMIN_URL}/connections`
        : `${process.env.REACT_APP_API_HR_URL}/connections`,
    [isConsultant],
  );

  /* ── Load company details + template on mount ─────────────────────── */
  useEffect(() => {
    const controller = new AbortController();

    if (isResend) {
      // ── RESEND PATH ──────────────────────────────────────────────────
      // Use the same company-details endpoint as the initial send to prefill
      // all form fields. No PDF parsing needed.
      if (!connectionId) {
        setLoading(false);
        setPrefillReady(true);
        return () => controller.abort();
      }

      setLoading(true);
      setPrefillReady(false);

      // Immediately prefill partner name from item card data if available
      if (prefillItem) {
        const partnerName =
          prefillItem.connectionCompanyName ||
          prefillItem.partnerName ||
          prefillItem.companyName ||
          "";
        if (partnerName) {
          setF((prev) => ({
            ...prev,
            consultancyName: partnerName,
            consultancyNoticeName: partnerName,
            consultancySignatoryName: partnerName,
          }));
        }
      }

      axios
        .get(`${HR_BASE}/company-details/${connectionId}`, {
          signal: controller.signal,
        })
        .then((r) => {
          const d = r.data?.data ?? r.data ?? {};
          if (!d || typeof d !== "object") return;

          const pick = (...keys) => keys.map((k) => d[k]).find(Boolean) || "";

          const ownerAddr = [
            pick("ownerAddressLine1", "companyAddressLine1", "addressLine1"),
            pick("ownerAddressLine2", "companyAddressLine2", "addressLine2"),
            pick("ownerCity", "companyCity", "city"),
            pick("ownerState", "companyState", "state"),
            pick("ownerCountry", "companyCountry", "country"),
            pick("ownerPincode", "companyPincode", "pincode", "zipCode"),
          ]
            .filter(Boolean)
            .join(", ");

          const ownerCompany = pick(
            "ownerCompanyName",
            "companyName",
            "ownerName",
            "name",
          );
          const ownerCeo = pick(
            "ownerCeo",
            "ceoName",
            "ceo",
            "ownerContactName",
            "contactName",
          );
          const ownerEmail = pick(
            "ownerEmail",
            "companyEmail",
            "email",
            "ownerContactEmail",
          );

          const partnerAddr = [
            pick("partnerAddressLine1", "consultantAddressLine1"),
            pick("partnerAddressLine2", "consultantAddressLine2"),
            pick("partnerCity", "consultantCity"),
            pick("partnerState", "consultantState"),
            pick("partnerCountry", "consultantCountry"),
            pick("partnerPincode", "consultantPincode"),
          ]
            .filter(Boolean)
            .join(", ");

          const partnerName = pick(
            "partnerName",
            "consultantName",
            "partnerCompanyName",
            "connectionCompanyName",
          );
          const partnerEmail = pick(
            "partnerEmail",
            "consultantEmail",
            "partnerContactEmail",
          );

          setF((prev) => ({
            ...prev,
            companyName: ownerCompany || prev.companyName,
            companyAddress: ownerAddr || prev.companyAddress,
            companyNoticeName:
              ownerCeo || ownerCompany || prev.companyNoticeName,
            companyNoticeAddress: ownerAddr || prev.companyNoticeAddress,
            companyNoticeEmail: ownerEmail || prev.companyNoticeEmail,
            companySignatoryName:
              ownerCeo || ownerCompany || prev.companySignatoryName,
            companySignatoryDesignation: prev.companySignatoryDesignation,
            consultancyName: partnerName || prev.consultancyName,
            consultancyAddress: partnerAddr || prev.consultancyAddress,
            consultancyNoticeName: partnerName || prev.consultancyNoticeName,
            consultancyNoticeAddress:
              partnerAddr || prev.consultancyNoticeAddress,
            consultancyNoticeEmail: partnerEmail || prev.consultancyNoticeEmail,
            consultancySignatoryName:
              partnerName || prev.consultancySignatoryName,
            consultancySignatoryDesignation:
              prev.consultancySignatoryDesignation,
          }));
        })
        .catch((err) => {
          if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError")
            return;
          // silently fall back to defaults / prefillItem values already set
        })
        .finally(() => {
          setLoading(false);
          setPrefillReady(true);
        });
    } else {
      // ── FIRST-TIME SEND PATH ─────────────────────────────────────────

      if (!connectionId) {
        setLoading(false);
        return () => controller.abort();
      }

      setLoading(true);

      // ── Immediately prefill from the item card data if available ──────
      if (prefillItem) {
        const partnerName =
          prefillItem.connectionCompanyName ||
          prefillItem.partnerName ||
          prefillItem.companyName ||
          "";
        if (partnerName) {
          setF((prev) => ({
            ...prev,
            consultancyName: partnerName,
            consultancyNoticeName: partnerName,
            consultancySignatoryName: partnerName,
          }));
        }
      }

      axios
        .get(`${HR_BASE}/company-details/${connectionId}`, {
          signal: controller.signal,
        })
        .then((r) => {
          // Support both r.data.data and r.data flat response shapes
          const d = r.data?.data ?? r.data ?? {};
          if (!d || typeof d !== "object") return;

          // Helper: pick first truthy value from a list of candidate keys
          const pick = (...keys) => keys.map((k) => d[k]).find(Boolean) || "";

          // ── Owner / Company side ───────────────────────────────────────
          const ownerAddr = [
            pick("ownerAddressLine1", "companyAddressLine1", "addressLine1"),
            pick("ownerAddressLine2", "companyAddressLine2", "addressLine2"),
            pick("ownerCity", "companyCity", "city"),
            pick("ownerState", "companyState", "state"),
            pick("ownerCountry", "companyCountry", "country"),
            pick("ownerPincode", "companyPincode", "pincode", "zipCode"),
          ]
            .filter(Boolean)
            .join(", ");

          const ownerCompany = pick(
            "ownerCompanyName",
            "companyName",
            "ownerName",
            "name",
          );
          const ownerCeo = pick(
            "ownerCeo",
            "ceoName",
            "ceo",
            "ownerContactName",
            "contactName",
          );
          const ownerEmail = pick(
            "ownerEmail",
            "companyEmail",
            "email",
            "ownerContactEmail",
          );

          // ── Partner / Consultancy side ─────────────────────────────────
          const partnerAddr = [
            pick("partnerAddressLine1", "consultantAddressLine1"),
            pick("partnerAddressLine2", "consultantAddressLine2"),
            pick("partnerCity", "consultantCity"),
            pick("partnerState", "consultantState"),
            pick("partnerCountry", "consultantCountry"),
            pick("partnerPincode", "consultantPincode"),
          ]
            .filter(Boolean)
            .join(", ");

          const partnerName = pick(
            "partnerName",
            "consultantName",
            "partnerCompanyName",
            "connectionCompanyName",
          );
          const partnerEmail = pick(
            "partnerEmail",
            "consultantEmail",
            "partnerContactEmail",
          );

          setF((prev) => ({
            ...prev,
            companyName: ownerCompany || prev.companyName,
            companyAddress: ownerAddr || prev.companyAddress,
            companyNoticeName:
              ownerCeo || ownerCompany || prev.companyNoticeName,
            companyNoticeAddress: ownerAddr || prev.companyNoticeAddress,
            companyNoticeEmail: ownerEmail || prev.companyNoticeEmail,
            companySignatoryName:
              ownerCeo || ownerCompany || prev.companySignatoryName,
            companySignatoryDesignation: prev.companySignatoryDesignation,
            consultancyName: partnerName || prev.consultancyName,
            consultancyAddress: partnerAddr || prev.consultancyAddress,
            consultancyNoticeName: partnerName || prev.consultancyNoticeName,
            consultancyNoticeAddress:
              partnerAddr || prev.consultancyNoticeAddress,
            consultancyNoticeEmail: partnerEmail || prev.consultancyNoticeEmail,
            consultancySignatoryName:
              partnerName || prev.consultancySignatoryName,
            consultancySignatoryDesignation:
              prev.consultancySignatoryDesignation,
          }));
        })
        .catch((err) => {
          if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError")
            return; // unmount abort — ignore
          // silently fall back to defaults / prefillItem values already set
        })
        .finally(() => setLoading(false));
    }

    return () => controller.abort();
  }, [connectionId, HR_BASE, isResend, moduleHistoryId, prefillItem]);

  /* ── Handle send ──────────────────────────────────────────────────── */
  const handleSubmit = useCallback(
    async (senderDocumentStatus) => {
      const isDraft = senderDocumentStatus === "DRAFT";
      setBusyType(isDraft ? "draft" : isResend ? "resend" : "send");
      setBusy(true);

      try {
        const html = buildAgreementHTML(f, annexure);
        const pdfBlob = await generatePDFBlob(html);
        await submitAgreement({
          moduleHistoryId,
          pdfBlob,
          senderDocumentStatus,
          isDraft,
          hrBase: HR_BASE,
        });
        // Only reaches here on a clean 2xx response
        toast.success(
          isDraft
            ? "Draft saved successfully!"
            : isResend
              ? "T&C resent successfully!"
              : "T&C sent successfully!",
        );
        if (onSave) onSave();
        onClose();
      } catch (err) {
        // axios throws for any non-2xx, so this only fires on real errors
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to submit agreement. Please try again.",
        );
      } finally {
        setBusy(false);
        setBusyType("");
      }
    },
    [f, annexure, moduleHistoryId, isResend, onSave, onClose, HR_BASE],
  );

  const doc = {
    fontFamily: "Georgia, serif",
    fontSize: 13,
    lineHeight: 1.9,
    color: "#1a1a1a",
  };
  const h3s = { fontSize: 13, fontWeight: "bold", margin: "16px 0 4px" };
  const ps = { margin: "3px 0" };

  /* ── Step label helper ────────────────────────────────────────────── */
  const TABS = [
    { n: 1, label: "Fill Agreement" },
    { n: 2, label: "Commercial Terms" },
    { n: 3, label: "Confirm & Send" },
  ];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.5)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onMouseDown={onClose}
    >
      <style>{`@keyframes ra-spin { to { transform: rotate(360deg); } }`}</style>

      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          width: "100%",
          maxWidth: 840,
          maxHeight: "92vh",
          overflowY: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 64px rgba(0,0,0,.3)",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div
          style={{
            padding: "14px 20px",
            borderBottom: "1px solid #f3f4f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>
              📋 Recruitment Consultancy Agreement
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {isResend && !prefillReady
                ? "⏳ Loading previous T&C values into fields…"
                : isResend
                  ? "✏️ Prefilled from previous T&C — review and update before resending"
                  : "Fill in the agreement details, review commercial terms, then send"}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 30,
              height: 30,
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              background: "#f9fafb",
              cursor: "pointer",
              fontSize: 14,
              color: "#6b7280",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>

        {/* ── Rejection Reason Banner (resend only) ── */}
        {isResend && rejectionReason && (
          <div
            style={{
              margin: "0 20px",
              marginTop: 10,
              padding: "10px 14px",
              background: "#fff7ed",
              border: "1px solid #fed7aa",
              borderRadius: 8,
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
            }}
          >
            <span style={{ fontSize: 15, flexShrink: 0 }}>⚠️</span>
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#c2410c",
                  marginBottom: 2,
                }}
              >
                Rejection reason
              </div>
              <div style={{ fontSize: 12, color: "#7c2d12", lineHeight: 1.5 }}>
                {rejectionReason}
              </div>
            </div>
          </div>
        )}

        {/* ── Step Tabs ── */}
        <div
          style={{
            display: "flex",
            borderBottom: "1px solid #e5e7eb",
            background: "#fafafa",
          }}
        >
          {TABS.map((tab) => (
            <button
              key={tab.n}
              onClick={() => !loading && setStep(tab.n)}
              style={{
                flex: 1,
                padding: "10px 8px",
                fontSize: 12,
                fontWeight: 600,
                border: "none",
                borderBottom: `2.5px solid ${step === tab.n ? "#2563eb" : "transparent"}`,
                background: "transparent",
                color: step === tab.n ? "#2563eb" : "#6b7280",
                cursor: loading ? "not-allowed" : "pointer",
                fontFamily: "inherit",
              }}
            >
              <span style={{ marginRight: 5 }}>
                {tab.n === step ? "●" : "○"}
              </span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Body ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}>
          {loading ? (
            <div
              style={{
                textAlign: "center",
                padding: "48px 0",
                color: "#6b7280",
                fontSize: 13,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: "3px solid #e5e7eb",
                  borderTopColor: "#2563eb",
                  animation: "ra-spin 0.65s linear infinite",
                  margin: "0 auto 12px",
                }}
              />
              Loading company details &amp; saved template…
            </div>
          ) : step === 1 ? (
            /* ════ STEP 1 — Fill Agreement ════ */
            <div style={doc}>
              <div
                style={{
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: 8,
                  padding: "8px 14px",
                  marginBottom: 16,
                  fontSize: 12,
                  color: "#92400e",
                }}
              >
                ✏️ Click any{" "}
                <span
                  style={{
                    background: "#eff6ff",
                    borderBottom: "1.5px solid #2563eb",
                    padding: "0 4px",
                    color: "#1e3a8a",
                    borderRadius: "2px 2px 0 0",
                    fontWeight: 600,
                  }}
                >
                  highlighted field
                </span>{" "}
                to edit. Fields pre-filled from company records.
              </div>

              {/* Title */}
              <h2
                style={{
                  fontSize: 15,
                  fontWeight: "bold",
                  textAlign: "center",
                  margin: "0 0 4px",
                }}
              >
                RECRUITMENT CONSULTANCY AGREEMENT
              </h2>
              <p
                style={{
                  textAlign: "center",
                  fontSize: 12,
                  color: "#555",
                  margin: "0 0 16px",
                }}
              >
                This Agreement is made and executed on this&nbsp;
                <input
                  value={f.day}
                  onChange={(e) => set("day")(e.target.value)}
                  placeholder="__"
                  style={{ ...SM_STYLE }}
                />
                &nbsp;day of&nbsp;
                <select
                  value={f.month}
                  onChange={(e) => set("month")(e.target.value)}
                  style={{ ...SM_STYLE, width: 104 }}
                >
                  <option value="">__ Month __</option>
                  {MONTHS.map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </select>
                &nbsp;
                <input
                  value={f.year}
                  onChange={(e) => set("year")(e.target.value)}
                  placeholder="20__"
                  style={{ ...SM_STYLE, width: 52 }}
                />
                &nbsp;at Bangalore, Karnataka.
              </p>

              {/* Parties */}
              <p style={ps}>
                <strong>between</strong>
              </p>
              <p style={ps}>
                <Wide
                  value={f["companyName"]}
                  onChange={set}
                  fKey="companyName"
                  placeholder="Company Name"
                  width={260}
                />
                , a company incorporated under the provisions of the Companies
                Act, 2013, having its registered office at{" "}
                <Wide
                  value={f["companyAddress"]}
                  onChange={set}
                  fKey="companyAddress"
                  placeholder="Company Registered Address"
                  width={320}
                />
                , hereinafter referred to as the <strong>"Company"</strong>;
              </p>
              <p style={ps}>
                <strong>AND</strong>
              </p>
              <p style={ps}>
                <Wide
                  value={f["consultancyName"]}
                  onChange={set}
                  fKey="consultancyName"
                  placeholder="[CONSULTANCY NAME]"
                  width={220}
                />
                , a&nbsp;
                <select
                  value={f.consultancyType}
                  onChange={(e) => set("consultancyType")(e.target.value)}
                  style={SEL_STYLE}
                >
                  {[
                    "Proprietorship",
                    "Partnership",
                    "Private Limited Company",
                    "LLP",
                    "OPC",
                  ].map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
                &nbsp;having its office at{" "}
                <Wide
                  value={f["consultancyAddress"]}
                  onChange={set}
                  fKey="consultancyAddress"
                  placeholder="[Full Address]"
                  width={280}
                />
                , hereinafter referred to as the <strong>"Consultant"</strong>.
              </p>

              {/* Clauses 1–3 — static */}
              <h3 style={h3s}>1. PURPOSE OF AGREEMENT</h3>
              <p style={ps}>
                The Company appoints the Consultant on a non-exclusive basis to
                provide recruitment and talent acquisition services for
                positions assigned by the Company from time to time, subject to
                the terms and conditions contained herein.
              </p>

              <h3 style={h3s}>2. SCOPE OF SERVICES</h3>
              <p style={ps}>
                The Consultant shall source suitable candidates, conduct
                preliminary screening and validation of profiles, share resumes
                of qualified candidates, coordinate interview schedules, support
                closure of selected candidates, and maintain professional
                conduct while representing the Company.
              </p>
              <p style={ps}>
                The Consultant shall <strong>not</strong> make commitments on
                behalf of the Company unless expressly authorized in writing,
                release confidential information, or use the Company's name/logo
                without written approval.
              </p>

              <h3 style={h3s}>3. ENGAGEMENT MODEL</h3>
              <p style={ps}>
                This Agreement shall operate on a requisition basis. The Company
                has full authority to accept or reject candidates, put
                requirements on hold, close positions internally, or work
                simultaneously with other consultants. No exclusivity is granted
                unless specifically agreed in writing.
              </p>

              {/* Clause 4 — fee (editable) */}
              <h3 style={h3s}>4. CONSULTANCY FEES</h3>
              <p style={ps}>
                <strong>4.1 Standard Fee</strong>
              </p>
              <p style={ps}>
                The Company shall pay the Consultant a professional fee of (
                <ABlank
                  value={annexure["recruitmentFee"]}
                  onChange={setA}
                  aKey="recruitmentFee"
                  type="number"
                  placeholder="8.33"
                  suffix="% of Annual Gross Salary"
                  width={60}
                />
                ).
              </p>
              <p style={ps}>
                <strong>4.2 Fee Applicability</strong> — Fees payable only if
                the candidate joins, was first introduced by the Consultant, and
                was not already in the Company database.
              </p>
              <p style={ps}>
                <strong>4.3 Duplicate Profiles</strong> — Fee payable only to
                the first source; determined by timestamped records.
              </p>

              {/* Clause 5 — payment (editable) */}
              <h3 style={h3s}>5. PAYMENT TERMS</h3>
              <p style={ps}>
                Payment within{" "}
                <ABlank
                  value={annexure["paymentDays"]}
                  onChange={setA}
                  aKey="paymentDays"
                  type="number"
                  placeholder="35"
                  suffix="days"
                  width={48}
                />{" "}
                of joining date and valid invoice submission. Subject to
                applicable TDS deductions. No advance payments.
              </p>

              {/* Clause 6 — replacement (editable) */}
              <h3 style={h3s}>6. FREE REPLACEMENT GUARANTEE</h3>
              <p style={ps}>
                If the candidate exits within{" "}
                <ABlank
                  value={annexure["replacementDays"]}
                  onChange={setA}
                  aKey="replacementDays"
                  type="number"
                  placeholder="90"
                  suffix="days"
                  width={48}
                />{" "}
                of joining, the Consultant shall provide one replacement at no
                cost within{" "}
                <ABlank
                  value={annexure["replacementDeadlineDays"]}
                  onChange={setA}
                  aKey="replacementDeadlineDays"
                  type="number"
                  placeholder="30"
                  suffix="days."
                  width={48}
                />
              </p>

              {/* Clauses 7–14 — static */}
              <h3 style={h3s}>7. CANDIDATE AUTHENTICITY &amp; LIABILITY</h3>
              <p style={ps}>
                All resumes shared with candidate consent. No fake or misleading
                information shall be submitted. Consultant liable for losses
                from knowingly fraudulent information.
              </p>

              <h3 style={h3s}>8. NON-SOLICITATION</h3>
              <p style={ps}>
                Consultant shall not solicit Company employees for any third
                party during the term and for <strong>24 months</strong>{" "}
                thereafter. Violation leads to damages and immediate
                termination.
              </p>

              <h3 style={h3s}>9. CONFIDENTIALITY</h3>
              <p style={ps}>
                Consultant shall maintain strict confidentiality of all Company
                information received during engagement.{" "}
                <strong>This clause survives termination.</strong>
              </p>

              <h3 style={h3s}>10. DATA PRIVACY &amp; COMPLIANCE</h3>
              <p style={ps}>
                Consultant shall comply with applicable Indian laws on data
                privacy, IT, employment, and anti-discrimination. Candidate data
                shall not be reused without consent.
              </p>

              <h3 style={h3s}>11. BACKGROUND VERIFICATION</h3>
              <p style={ps}>
                Company reserves the right to conduct BGV on all candidates.
                Material discrepancies may lead to offer revocation and fee
                recovery.
              </p>

              <h3 style={h3s}>12. INDEPENDENT CONTRACTOR</h3>
              <p style={ps}>
                Consultant is an independent contractor. No employer-employee
                relationship, partnership, or joint venture is created.
              </p>

              <h3 style={h3s}>13. LIMITATION OF LIABILITY</h3>
              <p style={ps}>
                Company's maximum liability shall not exceed the fee paid for
                the specific candidate concerned.
              </p>

              <h3 style={h3s}>14. INDEMNITY</h3>
              <p style={ps}>
                Consultant indemnifies Company against misrepresentation,
                third-party claims, confidentiality breaches, and violations of
                applicable laws.
              </p>

              {/* Clause 15 — termination notice (editable) */}
              <h3 style={h3s}>15. TERMINATION</h3>
              <p style={ps}>
                Company may terminate without cause by giving{" "}
                <ABlank
                  value={annexure["noticePeriod"]}
                  onChange={setA}
                  aKey="noticePeriod"
                  type="number"
                  placeholder="30"
                  suffix="days"
                  width={48}
                />{" "}
                written notice, or immediately for misconduct, fraud, or
                confidentiality breach.
              </p>

              <h3 style={h3s}>16. FORCE MAJEURE</h3>
              <p style={ps}>
                Neither Party liable for delays caused by events beyond
                reasonable control including natural disasters, pandemic, war,
                or cyber incidents.
              </p>

              {/* Clause 17 — jurisdiction (editable) */}
              <h3 style={h3s}>17. GOVERNING LAW &amp; JURISDICTION</h3>
              <p style={ps}>
                Governed by the laws of India. Exclusive jurisdiction:{" "}
                <input
                  value={annexure.jurisdiction}
                  onChange={(e) => setA("jurisdiction")(e.target.value)}
                  placeholder="Bangalore, Karnataka"
                  style={{ ...INP_STYLE, width: 180 }}
                />
                .
              </p>

              <h3 style={h3s}>18. DISPUTE RESOLUTION</h3>
              <p style={ps}>
                Amicable resolution first; if unresolved within 30 days,
                arbitration under the Arbitration and Conciliation Act, 1996.
                Venue: {annexure.jurisdiction}. One arbitrator. Decision final
                and binding.
              </p>

              {/* Clause 19 — Notice details (editable) */}
              <h3 style={h3s}>19. NOTICE</h3>
              <p style={ps}>
                All notices via registered post, courier, or official email.
              </p>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                  marginTop: 8,
                }}
              >
                <tbody>
                  <tr>
                    <td
                      style={{
                        verticalAlign: "top",
                        width: "50%",
                        paddingRight: 12,
                        borderRight: "1px solid #e5e7eb",
                      }}
                    >
                      <strong>Company</strong>
                      <br />
                      Name:{" "}
                      <Blank
                        value={f["companyNoticeName"]}
                        onChange={set}
                        fKey="companyNoticeName"
                        placeholder="Contact name"
                        width={160}
                      />
                      <br />
                      Address:{" "}
                      <Blank
                        value={f["companyNoticeAddress"]}
                        onChange={set}
                        fKey="companyNoticeAddress"
                        placeholder="Address"
                        width={160}
                      />
                      <br />
                      Email:{" "}
                      <Blank
                        value={f["companyNoticeEmail"]}
                        onChange={set}
                        fKey="companyNoticeEmail"
                        placeholder="email@company.com"
                        width={160}
                      />
                    </td>
                    <td style={{ verticalAlign: "top", paddingLeft: 12 }}>
                      <strong>Consultant</strong>
                      <br />
                      Name:{" "}
                      <Blank
                        value={f["consultancyNoticeName"]}
                        onChange={set}
                        fKey="consultancyNoticeName"
                        placeholder="Contact name"
                        width={160}
                      />
                      <br />
                      Address:{" "}
                      <Blank
                        value={f["consultancyNoticeAddress"]}
                        onChange={set}
                        fKey="consultancyNoticeAddress"
                        placeholder="Address"
                        width={160}
                      />
                      <br />
                      Email:{" "}
                      <Blank
                        value={f["consultancyNoticeEmail"]}
                        onChange={set}
                        fKey="consultancyNoticeEmail"
                        placeholder="email@consultant.com"
                        width={160}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              <h3 style={h3s}>20. ENTIRE AGREEMENT</h3>
              <p style={ps}>
                This Agreement supersedes all prior discussions. Amendments
                valid only if in writing and signed by both Parties.
              </p>

              {/* Clause 21 — validity (editable) */}
              <h3 style={h3s}>21. VALIDITY</h3>
              <p style={ps}>
                This Agreement shall remain valid for{" "}
                <input
                  value={annexure.agreementValidity}
                  onChange={(e) => setA("agreementValidity")(e.target.value)}
                  placeholder="3 Years"
                  style={{ ...INP_STYLE, width: 80 }}
                />{" "}
                from the Effective Date unless terminated earlier.
              </p>

              {/* Clause 22 — Signatures (editable) */}
              <h3 style={h3s}>22. ACCEPTANCE &amp; SIGNATURES</h3>
              <p style={ps}>
                IN WITNESS WHEREOF, the Parties hereto have executed this
                Agreement on the date first mentioned above.
              </p>
              <div style={{ display: "flex", gap: 32, marginTop: 12 }}>
                <div
                  style={{
                    flex: 1,
                    borderTop: "1px solid #999",
                    paddingTop: 8,
                    fontSize: 12,
                  }}
                >
                  <strong>For the Company</strong>
                  <br />
                  Name:{" "}
                  <Blank
                    value={f["companySignatoryName"]}
                    onChange={set}
                    fKey="companySignatoryName"
                    placeholder="Signatory name"
                    width={150}
                  />
                  <br />
                  <br />
                  Designation:{" "}
                  <Blank
                    value={f["companySignatoryDesignation"]}
                    onChange={set}
                    fKey="companySignatoryDesignation"
                    placeholder="CEO / HR Head"
                    width={140}
                  />
                  <br />
                  <br />
                  Seal &amp; Signature: _______________
                </div>
                <div
                  style={{
                    flex: 1,
                    borderTop: "1px solid #999",
                    paddingTop: 8,
                    fontSize: 12,
                  }}
                >
                  <strong>For the Recruitment Consultancy</strong>
                  <br />
                  Name:{" "}
                  <Blank
                    value={f["consultancySignatoryName"]}
                    onChange={set}
                    fKey="consultancySignatoryName"
                    placeholder="Signatory name"
                    width={150}
                  />
                  <br />
                  <br />
                  Designation:{" "}
                  <Blank
                    value={f["consultancySignatoryDesignation"]}
                    onChange={set}
                    fKey="consultancySignatoryDesignation"
                    placeholder="Founder / Director"
                    width={140}
                  />
                  <br />
                  <br />
                  Seal &amp; Signature: _______________
                </div>
              </div>
            </div>
          ) : step === 2 ? (
            /* ════ STEP 2 — Commercial Terms ════ */
            <div>
              <div
                style={{
                  background: "#eff6ff",
                  borderRadius: 8,
                  padding: "10px 14px",
                  fontSize: 13,
                  color: "#1d4ed8",
                  marginBottom: 16,
                }}
              >
                💡 These values are reflected throughout the agreement. Edit
                here or inline in Step 1 — they stay in sync.
              </div>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr style={{ background: "#f3f4f6" }}>
                    <th
                      style={{
                        padding: "8px 12px",
                        border: "1px solid #e5e7eb",
                        textAlign: "left",
                      }}
                    >
                      Particulars
                    </th>
                    <th
                      style={{
                        padding: "8px 12px",
                        border: "1px solid #e5e7eb",
                        textAlign: "left",
                      }}
                    >
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      label: "Recruitment Fee",
                      key: "recruitmentFee",
                      suffix: "% of Annual Gross Salary",
                      type: "number",
                    },
                    {
                      label: "Payment Within",
                      key: "paymentDays",
                      suffix: "days of joining",
                      type: "number",
                    },
                    {
                      label: "Replacement Period",
                      key: "replacementDays",
                      suffix: "days",
                      type: "number",
                    },
                    {
                      label: "Replacement Delivery",
                      key: "replacementDeadlineDays",
                      suffix: "days",
                      type: "number",
                    },
                    {
                      label: "Termination Notice",
                      key: "noticePeriod",
                      suffix: "days",
                      type: "number",
                    },
                    {
                      label: "Agreement Validity",
                      key: "agreementValidity",
                      suffix: "",
                      type: "text",
                    },
                    {
                      label: "Jurisdiction",
                      key: "jurisdiction",
                      suffix: "",
                      type: "text",
                    },
                  ].map((row) => (
                    <tr key={row.key}>
                      <td
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #e5e7eb",
                          color: "#374151",
                        }}
                      >
                        {row.label}
                      </td>
                      <td
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        <input
                          type={row.type}
                          value={annexure[row.key]}
                          onChange={(e) => setA(row.key)(e.target.value)}
                          style={{
                            padding: "4px 8px",
                            border: "1px solid #d1d5db",
                            borderRadius: 6,
                            fontSize: 13,
                            width: row.type === "number" ? 80 : 180,
                            fontFamily: "inherit",
                          }}
                        />
                        {row.suffix && (
                          <span
                            style={{
                              marginLeft: 6,
                              color: "#6b7280",
                              fontSize: 12,
                            }}
                          >
                            {row.suffix}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {[
                    ["GST Applicability", "Extra as applicable"],
                    ["TDS Deduction", "As per Income Tax Act"],
                  ].map(([k, val]) => (
                    <tr key={k}>
                      <td
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #e5e7eb",
                          color: "#374151",
                        }}
                      >
                        {k}
                      </td>
                      <td
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #e5e7eb",
                          color: "#6b7280",
                          fontSize: 12,
                        }}
                      >
                        {val} (fixed)
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* ════ STEP 3 — Confirm & Send ════ */
            <div>
              {isResend && prefillReady && (
                <div
                  style={{
                    background: "#eff6ff",
                    borderRadius: 8,
                    padding: "10px 14px",
                    fontSize: 13,
                    color: "#1d4ed8",
                    marginBottom: 14,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      border: "2px solid #bfdbfe",
                      borderTopColor: "#2563eb",
                      animation: "ra-spin 0.65s linear infinite",
                      flexShrink: 0,
                    }}
                  />
                  Loading previous T&C values into fields…
                </div>
              )}
              {isResend && prefillReady && (
                <div
                  style={{
                    background: "#fef3c7",
                    borderRadius: 8,
                    padding: "10px 14px",
                    fontSize: 13,
                    color: "#d97706",
                    marginBottom: 14,
                  }}
                >
                  ✏️ Fields have been prefilled from your previous T&C — review
                  and update before resending.
                </div>
              )}
              <div
                style={{
                  background: "#f0fdf4",
                  borderRadius: 8,
                  padding: "10px 14px",
                  fontSize: 13,
                  color: "#15803d",
                  marginBottom: 14,
                }}
              >
                ✅ Review the summary below. Clicking{" "}
                <strong>{isResend ? "Resend T&C" : "Send T&C"}</strong> will
                generate a PDF and submit it to the other party.
              </div>

              {/* Summary table */}
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                  marginBottom: 16,
                }}
              >
                <thead>
                  <tr style={{ background: "#f9fafb" }}>
                    <th
                      style={{
                        padding: "8px 14px",
                        border: "1px solid #e5e7eb",
                        textAlign: "left",
                      }}
                    >
                      Field
                    </th>
                    <th
                      style={{
                        padding: "8px 14px",
                        border: "1px solid #e5e7eb",
                        textAlign: "left",
                      }}
                    >
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Date", `${f.day || "—"} ${f.month || "—"} ${f.year}`],
                    ["Company Name", f.companyName || "—"],
                    ["Consultancy Name", f.consultancyName || "—"],
                    ["Entity Type", f.consultancyType],
                    ["Consultancy Address", f.consultancyAddress || "—"],
                    ["Company Signatory", f.companySignatoryName || "—"],
                    [
                      "Consultancy Signatory",
                      f.consultancySignatoryName || "—",
                    ],
                    [
                      "Recruitment Fee",
                      `${annexure.recruitmentFee}% of Annual Gross Salary`,
                    ],
                    [
                      "Payment Terms",
                      `Within ${annexure.paymentDays} days of joining`,
                    ],
                    ["Replacement Period", `${annexure.replacementDays} days`],
                    [
                      "Replacement Delivery",
                      `${annexure.replacementDeadlineDays} days`,
                    ],
                    ["Termination Notice", `${annexure.noticePeriod} days`],
                    ["Agreement Validity", annexure.agreementValidity],
                    ["Jurisdiction", annexure.jurisdiction],
                  ].map(([k, val]) => (
                    <tr key={k}>
                      <td
                        style={{
                          padding: "7px 14px",
                          border: "1px solid #e5e7eb",
                          color: "#6b7280",
                          fontWeight: 600,
                        }}
                      >
                        {k}
                      </td>
                      <td
                        style={{
                          padding: "7px 14px",
                          border: "1px solid #e5e7eb",
                          color: "#111827",
                        }}
                      >
                        {val}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  padding: "10px 14px",
                  fontSize: 12,
                  color: "#6b7280",
                }}
              >
                📄 A PDF will be generated from the filled agreement and
                submitted securely.
                {!isResend &&
                  " You can also save a draft if you're not ready to send yet."}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div
          style={{
            padding: "12px 20px",
            borderTop: "1px solid #f3f4f6",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#fafafa",
            borderRadius: "0 0 16px 16px",
          }}
        >
          {/* Left — Cancel + Back */}
          <div style={{ display: "flex", gap: 8 }}>
            <Btn v="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Btn>
            {step > 1 && (
              <Btn
                v="neutral"
                onClick={() => setStep((s) => s - 1)}
                disabled={busy}
              >
                ← Back
              </Btn>
            )}
          </div>

          {/* Right — Next / action buttons */}
          <div style={{ display: "flex", gap: 8 }}>
            {step < 3 && (
              <Btn
                v="primary"
                onClick={() => setStep((s) => s + 1)}
                disabled={loading}
              >
                Next →
              </Btn>
            )}
            {step === 3 && !isResend && (
              <Btn
                v="outline"
                loading={busy && busyType === "draft"}
                disabled={busy}
                onClick={() => handleSubmit("DRAFT")}
              >
                💾 Save as Draft
              </Btn>
            )}
            {step === 3 && (
              <Btn
                v={isResend ? "amber" : "success"}
                loading={busy && (busyType === "send" || busyType === "resend")}
                disabled={busy}
                onClick={() => handleSubmit(isResend ? "RESENT" : "SENT")}
              >
                {busy && (busyType === "send" || busyType === "resend")
                  ? "Generating PDF…"
                  : isResend
                    ? "↻ Resend T&C"
                    : "📤 Send T&C"}
              </Btn>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecruitmentAgreementModal;
